/**
 * Dev-sync J1 — post-render label de-collision.
 *
 * FULL port of legacy declutterLabels + its anchor helpers
 * (psychrometric_chart.js:229-286 and :706-834). The survey (§A1 / risk 3)
 * flagged this as the highest-risk item because it reads Plotly-INTERNAL
 * rendered geometry; the risk is contained here by (a) loading the very same
 * vendored Plotly build legacy ran (usePlotly.ts — byte-identical renderer,
 * so the internals read here are the internals legacy read), and (b) keeping
 * every read defensive (`_fullLayout`/`c2p`/`_length` guards, js:737-741) so
 * a future Plotly swap degrades to "labels keep their initial anchors", not
 * a crash. It was ported rather than simplified because it is a
 * self-contained pure pass over (node, traces) — the expensive-looking part
 * of the survey's ~340-line estimate turned out to be the cluster/role/label
 * BUILDERS, which live in chartTraces.ts and were needed for hover content
 * anyway.
 *
 * What it does (js:706-735): runs once per render, after Plotly has drawn
 * (real label sizes available), and nudges only labels actually in conflict
 * to the nearest clear anchor around their own point — never touching text,
 * font, or which labels exist. Geometry is computed in Plotly's own internal
 * plot-area pixel space (xaxis/yaxis.c2p() + SVG getBBox()), NOT
 * getBoundingClientRect() — the popup's open animation starts the dialog at
 * transform: scale(0,0), which poisons client rects on the very first render
 * but leaves the SVG's internal coordinates correct.
 */

import type { PlotlyChartNode, PlotlyStatic, PsyScatterTrace } from './usePlotly';

interface LabelAnchor {
  key: string;
  h: 'left' | 'center' | 'right';
  v: 'top' | 'middle' | 'bottom';
}

/** Eight compass anchors; "middle center" deliberately excluded — it would
 * sit directly on the dot (js:225-238). */
const LABEL_ANCHORS: LabelAnchor[] = [
  { key: 'top left', h: 'left', v: 'top' },
  { key: 'top center', h: 'center', v: 'top' },
  { key: 'top right', h: 'right', v: 'top' },
  { key: 'middle left', h: 'left', v: 'middle' },
  { key: 'middle right', h: 'right', v: 'middle' },
  { key: 'bottom left', h: 'left', v: 'bottom' },
  { key: 'bottom center', h: 'center', v: 'bottom' },
  { key: 'bottom right', h: 'right', v: 'bottom' },
];

function labelAnchorDef(key: string): LabelAnchor {
  for (let i = 0; i < LABEL_ANCHORS.length; i++) {
    if (LABEL_ANCHORS[i].key === key) return LABEL_ANCHORS[i];
  }
  return LABEL_ANCHORS[1]; // "top center" — the chart's own default
}

/** Fixed try-order; middle-left/right are a last resort (js:245-252). */
const LABEL_ANCHOR_PRIORITY = [
  'top center',
  'bottom center',
  'top right',
  'bottom left',
  'top left',
  'bottom right',
  'middle right',
  'middle left',
];

/** Green user-annotation labels always try "top right" first (js:253-262). */
function orderedLabelAnchors(isAnnotation: boolean): LabelAnchor[] {
  const order = isAnnotation
    ? ['top right'].concat(LABEL_ANCHOR_PRIORITY.filter((k) => k !== 'top right'))
    : LABEL_ANCHOR_PRIORITY;
  return order.map(labelAnchorDef);
}

interface LabelInfo {
  ti: number;
  pi: number;
  anchor: string;
  finalAnchor: string;
  width: number;
  height: number;
  markerCX: number;
  markerCY: number;
  markerRadius: number;
}

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Predicts the screen-space box a label would occupy at `anchor` around its
 * marker — width/height come from the label's actual rendered size, which is
 * anchor-invariant (js:263-277). */
function candidateLabelBox(info: LabelInfo, anchor: LabelAnchor, gap: number): Box {
  const w = info.width;
  const h = info.height;
  const standoff = info.markerRadius + gap;
  const left =
    anchor.h === 'left'
      ? info.markerCX - standoff - w
      : anchor.h === 'right'
        ? info.markerCX + standoff
        : info.markerCX - w / 2;
  const top =
    anchor.v === 'top'
      ? info.markerCY - standoff - h
      : anchor.v === 'bottom'
        ? info.markerCY + standoff
        : info.markerCY - h / 2;
  return { left, top, right: left + w, bottom: top + h };
}

function expandBox(box: Box, pad: number): Box {
  return { left: box.left - pad, right: box.right + pad, top: box.top - pad, bottom: box.bottom + pad };
}

function boxOverlapArea(a: Box, b: Box): number {
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.right, b.right);
  const top = Math.max(a.top, b.top);
  const bottom = Math.min(a.bottom, b.bottom);
  const w = right - left;
  const h = bottom - top;
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * js:736-834. Only markers+text traces carry labels; line-only traces are
 * skipped. A trace's position in `traces` lines up 1:1 with its
 * `.scatterlayer .trace` DOM node and curveNumber, and each trace's Nth
 * `.point`/`.textpoint` is that trace's Nth data point (index join, no key
 * function — see the legacy note at js:842-852).
 */
export function declutterLabels(
  plotly: PlotlyStatic,
  node: PlotlyChartNode | null,
  traces: PsyScatterTrace[]
): void {
  if (!node || !node._fullLayout) return;
  const xaxis = node._fullLayout.xaxis;
  const yaxis = node._fullLayout.yaxis;
  if (!xaxis || !yaxis || typeof xaxis.c2p !== 'function' || typeof yaxis.c2p !== 'function') return;
  if (!xaxis._length || !yaxis._length) return;
  const bounds: Box = { left: 0, top: 0, right: xaxis._length, bottom: yaxis._length };

  // Gap kept between a label and the marker it's anchored to/around (js:744-747).
  const GAP = 3;
  const traceEls = node.querySelectorAll('.scatterlayer .trace');
  const markerBoxes: { ti: number; pi: number; box: Box }[] = [];
  const labelInfos: LabelInfo[] = [];

  traces.forEach((trace, ti) => {
    const el = traceEls[ti];
    if (trace.mode !== 'markers+text' || !el) return;
    const pointEls = el.querySelectorAll('.point');
    const textEls = el.querySelectorAll('.textpoint');
    const sizeAttr = trace.marker && trace.marker.size;
    const xs = trace.x || [];
    const ys = trace.y || [];
    (trace.text || []).forEach((text, pi) => {
      const pointEl = pointEls[pi];
      if (!pointEl || xs[pi] == null || ys[pi] == null) return;
      const markerSize = Array.isArray(sizeAttr) ? sizeAttr[pi] || 11 : sizeAttr || 11;
      const cx = xaxis.c2p!(xs[pi] as number);
      const cy = yaxis.c2p!(ys[pi] as number);
      markerBoxes.push({
        ti,
        pi,
        box: {
          left: cx - markerSize / 2,
          right: cx + markerSize / 2,
          top: cy - markerSize / 2,
          bottom: cy + markerSize / 2,
        },
      });
      if (!text) return;
      const textG = textEls[pi];
      const textEl = textG && textG.querySelector('text');
      if (!textEl) return;
      const bbox = (textEl as SVGGraphicsElement).getBBox();
      if (!bbox.width && !bbox.height) return;
      const anchor = Array.isArray(trace.textposition)
        ? trace.textposition[pi]
        : trace.textposition || 'top center';
      labelInfos.push({
        ti,
        pi,
        anchor,
        finalAnchor: anchor,
        width: bbox.width,
        height: bbox.height,
        markerCX: cx,
        markerCY: cy,
        markerRadius: markerSize / 2,
      });
    });
  });
  if (!labelInfos.length) return;

  labelInfos.sort((a, b) => a.ti - b.ti || a.pi - b.pi);

  const placedBoxes: Box[] = []; // finalized labels — later labels steer clear of earlier ones
  labelInfos.forEach((info) => {
    const otherMarkers = markerBoxes.filter((m) => m.ti !== info.ti || m.pi !== info.pi);
    const candidates = orderedLabelAnchors(traces[info.ti].name === 'Annotations');
    let bestBox: Box | null = null;
    let bestAnchor = info.anchor;
    let bestScore = Infinity;
    for (let c = 0; c < candidates.length; c++) {
      const anchorDef = candidates[c];
      const box = candidateLabelBox(info, anchorDef, GAP);
      const inBounds =
        box.left >= bounds.left &&
        box.right <= bounds.right &&
        box.top >= bounds.top &&
        box.bottom <= bounds.bottom;
      let score = 0;
      otherMarkers.forEach((m) => {
        score += boxOverlapArea(expandBox(box, GAP), m.box);
      });
      placedBoxes.forEach((p) => {
        score += boxOverlapArea(expandBox(box, GAP), p);
      });
      if (!inBounds) score += 1e6; // keep-inside-chart is a strong preference, not a veto
      if (score === 0) {
        bestBox = box;
        bestAnchor = anchorDef.key;
        bestScore = 0;
        break;
      }
      if (score < bestScore) {
        bestScore = score;
        bestBox = box;
        bestAnchor = anchorDef.key;
      }
    }
    info.finalAnchor = bestAnchor;
    if (bestBox) placedBoxes.push(bestBox);
  });

  const traceArrays: Record<number, string[]> = {};
  function textPositionArray(ti: number): string[] {
    if (!traceArrays[ti]) {
      const trace = traces[ti];
      const n = (trace.text || []).length;
      const v = trace.textposition;
      if (Array.isArray(v)) {
        traceArrays[ti] = v.slice();
      } else {
        const arr: string[] = [];
        for (let i = 0; i < n; i++) arr.push(v || 'top center');
        traceArrays[ti] = arr;
      }
    }
    return traceArrays[ti];
  }
  const touchedTis: number[] = [];
  labelInfos.forEach((info) => {
    if (info.finalAnchor === info.anchor) return;
    const arr = textPositionArray(info.ti);
    arr[info.pi] = info.finalAnchor;
    if (touchedTis.indexOf(info.ti) === -1) touchedTis.push(info.ti);
  });
  if (!touchedTis.length) return;
  touchedTis.forEach((ti) => {
    traces[ti].textposition = traceArrays[ti];
  });
  void plotly.restyle(
    node,
    { textposition: touchedTis.map((ti) => traceArrays[ti]) },
    touchedTis
  );
}
